const AUTH_URL = import.meta.env?.VITE_AUTH_URL || 'https://auth.teliance.com';
const CLIENT_ID = 'teliance-events';
const REFRESH_LOCK = 'teliance-events-oauth-refresh';
const RECENT_SYNC_MS = 30000;

const AUTH_KEYS = [
  'token',
  'refreshToken',
  'userEmail',
  'userName',
  'userRole',
  'ssoLastSyncAt',
];

let refreshInFlight = null;

function decodeJwtPayload(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function dispatchAuthEvent(eventTarget, name, detail) {
  if (!eventTarget?.dispatchEvent) return;
  const event = typeof CustomEvent === 'function'
    ? new CustomEvent(name, { detail })
    : { type: name, detail };
  eventTarget.dispatchEvent(event);
}

export function clearStoredAuth(storage = localStorage) {
  AUTH_KEYS.forEach((key) => storage.removeItem(key));
}

function currentSession(storage) {
  const token = storage.getItem('token');
  if (!token) return { status: 'invalid', token: null, user: null };
  const claims = decodeJwtPayload(token) || {};
  return {
    status: 'ok',
    token,
    user: {
      email: claims.email || storage.getItem('userEmail') || '',
      username: claims.username || storage.getItem('userName') || '',
      role: claims.role || storage.getItem('userRole') || '',
    },
  };
}

async function performRefresh({ fetchImpl, storage, eventTarget, now }) {
  // Read inside the cross-tab lock. Another tab may have rotated the token
  // while this caller was waiting for the lock.
  const refreshToken = storage.getItem('refreshToken');
  if (!refreshToken) {
    clearStoredAuth(storage);
    dispatchAuthEvent(eventTarget, 'auth:expired');
    return { status: 'invalid', token: null, user: null };
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: CLIENT_ID,
  });

  let response;
  try {
    response = await fetchImpl(`${AUTH_URL}/token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      signal: AbortSignal.timeout(10000),
    });
  } catch (error) {
    console.error('[Auth] OAuth refresh unavailable:', error);
    return { status: 'unavailable', token: null, user: null };
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 400 || response.status === 401) {
      clearStoredAuth(storage);
      dispatchAuthEvent(eventTarget, 'auth:expired');
      return { status: 'invalid', token: null, user: null };
    }
    return { status: 'unavailable', token: null, user: null };
  }

  if (!data.access_token || !data.refresh_token) {
    return { status: 'unavailable', token: null, user: null };
  }

  const claims = decodeJwtPayload(data.access_token) || {};
  const user = {
    email: claims.email || '',
    username: claims.username || '',
    role: claims.role || '',
  };
  storage.setItem('token', data.access_token);
  storage.setItem('refreshToken', data.refresh_token);
  storage.setItem('userEmail', user.email);
  storage.setItem('userName', user.username);
  storage.setItem('userRole', user.role);
  storage.setItem('ssoLastSyncAt', String(now()));
  dispatchAuthEvent(eventTarget, 'auth:refreshed', { token: data.access_token, user });
  return { status: 'ok', token: data.access_token, user };
}

export function refreshCentralSession(options = {}) {
  if (refreshInFlight) return refreshInFlight;

  const fetchImpl = options.fetchImpl || fetch;
  const storage = options.storage || localStorage;
  const eventTarget = options.eventTarget || window;
  const lockManager = options.lockManager || globalThis.navigator?.locks;
  const now = options.now || Date.now;
  const force = options.force === true;

  refreshInFlight = (async () => {
    const refresh = async () => {
      const lastSync = Number(storage.getItem('ssoLastSyncAt') || 0);
      if (!force && lastSync && now() - lastSync < RECENT_SYNC_MS) {
        return currentSession(storage);
      }
      return performRefresh({ fetchImpl, storage, eventTarget, now });
    };

    try {
      if (lockManager?.request) {
        return await lockManager.request(REFRESH_LOCK, refresh);
      }
      return await refresh();
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export function resetAuthSessionForTests() {
  refreshInFlight = null;
}
